/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { Touchable } from "frontend/src/components/Touchable"
import Img from "gatsby-image"
import { getFluidHeightFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { displayCent } from "frontend/src/utils/displayCent"
import { Flex } from "../../components/Layout"
import { SocialProof } from "frontend/src/components/SocialProof"

function ProductVariantBadge({ color, text }) {
  return (
    <div
      sx={{
        borderRadius: "9999px",
        bg: color,
        color: "white",
        fontFamily: "heading",
        fontSize: 0,
        fontWeight: 500,
        px: 5,
        py: 3,
        display: "inline-flex",
        position: "absolute",
        top: 0,
        transform: "translate(-50%, -50%)",
        left: "50%",
      }}
    >
      {text}
    </div>
  )
}

function NotAvailable() {
  return (
    <span
      sx={{ fontSize: [2, 2, 2, 3], fontFamily: "body", color: "accent", fontWeight: "medium" }}
    >
      aktuell ausverkauft &ndash; längere Wartezeiten
    </span>
  )
}

function ProductVariantCard({ variant, index, onClick, active, cardStyles }) {
  const badgeColor = index === 2 ? "purple_300" : index === 1 ? "#FF9B00" : "almostBlack"
  const {
    tagWord,
    toggleVariantName,
    name,
    image,
    cartName,
    price,
    comparePrice = undefined,
    available,
    toggleUnitPrice,
    portionContent,
    reference_size,
    reference_unit,
    package_size,
    socialProof,
  } = variant
  let referencePrice = 0
  if (toggleUnitPrice) {
    referencePrice = Math.round((price * reference_size) / package_size)
  }
  return (
    <Touchable
      sx={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: active ? badgeColor : "transparent",
        px: [4, 4, 4, 6],
        py: [3, 4, 7],
        boxShadow: "0px 4.50221px 18.0089px rgba(0, 0, 0, 0.1)",
        bg: "white",
        height: "100%",
        width: "100%",
        position: "relative",
        ...cardStyles,
      }}
      onClick={onClick}
    >
      <ProductVariantBadge color={badgeColor} text={tagWord} />
      <Flex
        sx={{ height: "100%", textAlign: "center" }}
        direction="column"
        align="center"
        justify="space-between"
      >
        {toggleVariantName ? (
          <h4
            sx={{ variant: "text.h3", textTransform: "uppercase", fontSize: [3, 3, 4, 6], mt: 5 }}
          >
            {name}
          </h4>
        ) : (
          <div sx={{ height: "20px" }} />
        )}
        <div sx={{ height: ["120px", "150px", "210px"], my: [2, 2, 2, 12], width: "100%" }}>
          <Img
            fluid={getFluidHeightFromAsset(image, 1000)}
            imgStyle={{ objectFit: "contain" }}
            sx={{ height: "100%" }}
          />
        </div>
        <Flex direction="column" align="center">
          {!available && <NotAvailable />}
          <span sx={{ fontSize: [1, 1, 1, 3] }}>
            {portionContent} {cartName}
          </span>
          <SocialProof data={socialProof} />
          <span
            sx={{
              mt: 4,
              fontSize: [4, 4, 4, 6],
              fontWeight: "bold",
              fontFamily: "heading",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {comparePrice && (
              // <del sx={{ mr: 4, color: "accent", fontSize: "0.7em" }}>
              //   {displayCent(comparePrice)}
              // </del>
              <span
                sx={{
                  mr: 4,
                  color: "accent",
                  fontSize: "0.7em",
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gridTemplateRows: "1fr",
                }}
              >
                <span sx={{ gridColumn: "1 / span 1", gridRow: "1 / span 1" }}>
                  {displayCent(comparePrice)}
                </span>
                <hr sx={{ gridColumn: "1 / span 1", gridRow: "1 / span 1", width: "100%" }} />
              </span>
            )}
            {displayCent(price)}
          </span>
          {toggleUnitPrice && (
            <span sx={{ mt: 3, fontSize: [2, 2, 2, 3], fontFamily: "body" }}>
              ({displayCent(referencePrice)}/{reference_size} {reference_unit})
            </span>
          )}
        </Flex>
      </Flex>
    </Touchable>
  )
}

function VariantCard({ name, variant, index, onClick, active, cardStyles }) {
  const badgeColor = "almostBlack"
  const {
    tagWord,
    cartName,
    price,
    comparePrice = undefined,
    available,
    toggleUnitPrice,
    portionContent,
    reference_size,
    reference_unit,
    package_size,
    socialProof,
  } = variant
  let referencePrice = 0
  if (toggleUnitPrice) {
    referencePrice = Math.round((price * reference_size) / package_size)
  }
  return (
    <Touchable
      sx={{
        borderWidth: "1px",
        borderStyle: "solid",
        borderColor: active ? badgeColor : "transparent",
        px: [4, 4, 4, 6],
        py: [3, 4, 7],
        boxShadow: "0px 4.50221px 18.0089px rgba(0, 0, 0, 0.1)",
        bg: "white",
        height: "100%",
        width: "100%",
        position: "relative",
        ...cardStyles,
      }}
      onClick={onClick}
    >
      <ProductVariantBadge color={badgeColor} text={tagWord} />
      <Flex
        sx={{ height: "100%", textAlign: "center" }}
        direction="column"
        align="center"
        justify="space-between"
      >
        <h4 sx={{ variant: "text.h3", textTransform: "uppercase", fontSize: [3, 3, 4, 6], mt: 5 }}>
          {name}
        </h4>
        <Flex direction="column" align="center">
          {!available && <NotAvailable />}
          <span sx={{ fontSize: [1, 1, 1, 3], mt: 4 }}>
            {portionContent} {cartName}
          </span>
          <SocialProof data={socialProof} />
          <span
            sx={{
              mt: 4,
              fontSize: [4, 4, 4, 6],
              fontWeight: "bold",
              fontFamily: "heading",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {comparePrice && (
              <span
                sx={{
                  mr: 4,
                  color: "accent",
                  fontSize: "0.7em",
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gridTemplateRows: "1fr",
                }}
              >
                <span sx={{ gridColumn: "1 / span 1", gridRow: "1 / span 1" }}>
                  {displayCent(comparePrice)}
                </span>
                <hr sx={{ gridColumn: "1 / span 1", gridRow: "1 / span 1", width: "100%" }} />
              </span>
            )}
            {displayCent(price)}
          </span>
          {toggleUnitPrice ? (
            <span sx={{ mt: 3, fontSize: [2, 2, 2, 3], fontFamily: "body" }}>
              ({displayCent(referencePrice)}/{reference_size} {reference_unit})
            </span>
          ) : (
            <span sx={{ mt: 3, height: ["18px", "18px", "18px", "20px"] }} />
          )}
        </Flex>
      </Flex>
    </Touchable>
  )
}

export { ProductVariantCard, VariantCard }
